import axios from "axios";
import React, { useEffect, useState } from "react";
import { BASE_URL, Currencies, RoleEnum } from "./Constants";

const VIRTUAL_BRAND_COLOR = "#221780";

const AppContext = React.createContext({
  profile: null,
  isLoading: false,
  isLoggedIn: false,
  login: (profile) => {},
  logout: () => {},
  primaryColor: "#4f46e5",
  subscriptionStatus: "active",
  hasEventAccess: false,
  maxEventsCount: 0,
  isVirtual: false,
});

export const AppContextProvider = (props) => {
  const [profile, setProfile] = useState(() => {
    const profileString = localStorage.getItem("user_profile");
    if (profileString)
      axios.defaults.headers.common["Authorization"] = `Bearer ${
        JSON.parse(profileString).token
      }`;
    return profileString ? JSON.parse(profileString) : null;
  });

  const [primaryColor, setPrimaryColor] = useState("#000147"); // Default color
  const [secondaryColor, setSecondaryColor] = useState("#ffff");
  const [primaryTextColor, setPrimaryTextColor] = useState("#ffff");
  const [maxEmployees, setMaxEmployees] = useState(0);
  const [employeeUsage, setEmployeeUsage] = useState(0); // org-wide: Parent + all direct Sub-Companies
  const [subscriptionStatus, setStatus] = useState("active");
  const [currency, setCurrency] = useState("$");
  const [cardConfig, setCardConfig] = useState([]);
  const [logo, setLogo] = useState("/future_cards_logo.png"); // Default logo
  // Seeded from the login payload (like isVirtual) so /events does not bounce to My Packages
  // right after login, before /Company/settings has responded.
  const [hasEventAccess, setHasEventAccess] = useState(profile?.hasEventAccess || false);
  const [maxEventsCount, setMaxEventsCount] = useState(0);
  // Seeded from the login payload so virtual-only routes don't bounce on a page refresh
  // before /Company/settings has responded.
  const [isVirtual, setIsVirtual] = useState(profile?.isVirtual || false);

  useEffect(() => {
    // Set Axios authorization header on app load if profile exists
    if (profile?.token) {
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${profile.token}`;
      if (profile.role !== RoleEnum.PUBLIC_EMPLOYEE) fetchSettings();
    }
  }, [profile]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/Company/settings?id=${profile.companyId}`
      );
      if (response.data) {
        // Individual (virtual) accounts are created with the stock indigo and can't open Theme
        // Settings, so they get the platform brand blue instead.
        const storedColor = response.data.primaryColor;
        const useBrandBlue =
          response.data.isVirtual &&
          (!storedColor || storedColor.toLowerCase() === "#4f46e5");
        setPrimaryColor(useBrandBlue ? VIRTUAL_BRAND_COLOR : storedColor);
        setSecondaryColor(response.data.secondaryColor);
        setPrimaryTextColor(response.data.primaryTextColor);
        setCardConfig(response.data.digitalCardConfig);
        setStatus(response.data.subscriptionStatus);
        setMaxEmployees(response.data.maxEmployees ?? 0);
        if (response.data.currency)
          setCurrency(
            Currencies.find((i) => i.code === response.data.currency).symbol
          );
        setLogo(response.data.iconUrl || "/future_cards_logo.png");
        setHasEventAccess(response.data.hasEventAccess || false);
        setMaxEventsCount(response.data.maxEventsCount || 0);
        setIsVirtual(response.data.isVirtual || false);

        // Virtual (individual) companies cannot upload a logo, so use the platform default
        // set by the super-admin (Settings > Default Logos) when they have none of their own.
        if (response.data.isVirtual && !response.data.iconUrl) {
          try {
            const defaultLogo = await axios.get(
              `${BASE_URL}/Lookup/default-event-logo/virtual-portal`
            );
            if (defaultLogo.data?.logoUrl) setLogo(defaultLogo.data.logoUrl);
          } catch (error) {
            console.error("Error fetching default portal logo:", error);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }

    // Organization-wide employee usage (Parent + all direct Sub-Companies).
    // Convenience/informational only — the backend is authoritative on the limit.
    try {
      const usage = await axios.get(
        `${BASE_URL}/Employee/organization-usage?companyId=${profile.companyId}`
      );
      setEmployeeUsage(usage.data?.used ?? 0);
      if (usage.data?.max != null) setMaxEmployees(usage.data.max);
    } catch (error) {
      console.error("Error fetching organization usage:", error);
    }
  };

  const loginHandler = (userProfile) => {
    setProfile(userProfile);
    setIsVirtual(userProfile.isVirtual || false);
    setHasEventAccess(userProfile.hasEventAccess || false);
    axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${userProfile.token}`;
    localStorage.setItem("user_profile", JSON.stringify(userProfile));
  };

  const logoutHandler = () => {
    setProfile(null);
    localStorage.removeItem("user_profile");
    delete axios.defaults.headers.common["Authorization"]; // Clear the auth header
  };

  const authCtx = {
    profile: profile,
    maxEmployees: maxEmployees,
    employeeUsage: employeeUsage,
    isLoggedIn: !!profile?.token,
    primaryColor: primaryColor,
    secondaryColor: secondaryColor,
    navItemsColor: primaryTextColor,
    isExpired: subscriptionStatus === "Expired",
    companyLogo: logo,
    currency,
    cardConfig: cardConfig,
    hasEventAccess: hasEventAccess,
    maxEventsCount: maxEventsCount,
    isVirtual: isVirtual,
    setLogo: setLogo,
    setPrimaryColor: setPrimaryColor,
    setPrimaryTextColor: setPrimaryTextColor,
    setSecondaryColor: setSecondaryColor,
    login: loginHandler,
    logout: logoutHandler,
  };

  return (
    <AppContext.Provider value={authCtx}>{props.children}</AppContext.Provider>
  );
};

export default AppContext;
